/**
 * T-22 · La sección de un módulo, cargada bajo demanda.
 *
 * El fragmento se pide **cuando alguien entra a la sección**, no al abrir el
 * panel. Si la cafetería no contrató el módulo, `Armazon` ni siquiera enseña el
 * enlace, así que la petición no llega a existir.
 *
 * `T-36`: el `<h1>` de la sección se fue a la barra superior del armazón, que
 * es donde lo pone el handoff. Pintarlo también aquí lo enseñaría dos veces.
 *
 * `T-37`: un módulo puede tener **más de una sección**. «Fotografías» es del
 * módulo `menu` igual que el armador —sus rutas llevan
 * `@RequiereModulo('menu')`—, así que pasa por esta misma puerta: sin el módulo
 * contratado no se pide su fragmento, cerrado no se monta, y suspendido se
 * consulta. Lo único que cambia es **qué pantalla** se monta al otro lado, y eso
 * entra por `pantalla`.
 */
import { Suspense, type ReactNode } from 'react'
import { useOutletContext } from 'react-router'
import type { Modulo } from '@fialrest/shared'
import type { Yo } from '../api/sesion.js'
import { REGISTRO, type PantallaDeModulo } from '../modulos/registro.js'

export function SeccionDeModulo({ modulo, pantalla }: {
  readonly modulo: Modulo
  /**
   * Qué vista del módulo se monta. Por omisión, la principal del registro.
   *
   * Se recibe el componente ya construido en vez de un nombre que buscar en un
   * mapa: un mapa de nombres es una segunda tabla que se desincroniza, que es
   * justo lo que `registro.ts` evita al llamar a las carpetas como las claves
   * del catálogo. Aquí el compilador comprueba la forma y no hay nada que
   * traducir.
   */
  readonly pantalla?: PantallaDeModulo | undefined
}): ReactNode {
  const yo = useOutletContext<Yo>()
  const estado = yo.modulos[modulo]
  const Pantalla = pantalla ?? REGISTRO[modulo]

  return (
    <div style={{ display: 'grid', gap: 'var(--e4)' }}>
      {/* Sin módulo no se pide el fragmento. Llegar aquí sin contratarlo sólo
          pasa escribiendo la URL a mano — el servidor responde 402 igual, así
          que esto es cortesía, no protección. */}
      {estado === undefined ? (
        <p style={{ color: 'var(--neutro-600)', fontSize: 13 }}>
          Tu cafetería no tiene contratado este módulo.
        </p>
      ) : estado === 'closed' ? (
        /* `closed` no se administra — `ADR-011` y los predicados de `T-19`: un
           módulo cerrado apaga también lo público, «con una página digna». Sin
           esta rama el panel montaba el editor igual y la primera consulta se
           topaba con el 402 del servidor, así que quien cerró su módulo veía un
           error de red donde tocaba una explicación.

           Quien MANDA sigue siendo el servidor: esto no protege nada, evita
           enseñar una pantalla que no puede funcionar. Por eso se compara el
           estado a pelo en vez de traerse el predicado de la API — duplicar la
           regla de autorización en el navegador es peor que esta línea. */
        <p style={{ color: 'var(--neutro-600)', fontSize: 13 }}>
          Este módulo está <strong>cerrado</strong>. Escríbenos y lo reactivamos:
          tu información sigue guardada.
        </p>
      ) : (
        // El texto es corto a propósito: el fragmento pesa poco y en una red
        // normal esto parpadea. Un esqueleto animado se leería como un fallo.
        <Suspense fallback={<p style={{ color: 'var(--neutro-600)' }}>Cargando…</p>}>
          <Pantalla estado={estado} />
        </Suspense>
      )}
    </div>
  )
}
